import { parseDateFromDB } from '../services/taskService';
import type { Task } from '../types';

/**
 * Os números do cabeçalho do cronograma publicado.
 *
 * POR QUE ISTO EXISTE COMO MÓDULO PRÓPRIO
 * Até 28/08/2026 o cálculo vivia dentro de um `useMemo` do `SharedProjectView` e
 * misturava duas bases no mesmo cabeçalho: o anel de progresso era a média
 * aritmética simples do `progress` das tarefas RAIZ, e o contador ao lado
 * contava FOLHAS.
 *
 * O efeito medido, num projeto real do Ricardo: quatro raízes com 100, 55, 100 e
 * 100 davam 88,75, que virava "89%", enquanto as folhas eram 5 concluídas de 9,
 * ou seja 56%. Duas dessas raízes eram tarefas de teste ("Tarefa 1" e "o"), e
 * cada uma pesava tanto quanto uma fase inteira do projeto.
 *
 * Superestimar progresso é grave em qualquer tela e é inaceitável NESTA, que é
 * lida por quem não é usuário do produto e não tem como conferir. O padrão
 * obrigatório 5.3.1 do Código de Ética do PMI proíbe omitir informação que torne
 * a declaração enganosa.
 *
 * A REGRA, agora única
 * Progresso e contador compartilham a MESMA base: tarefas-folha contáveis, ou
 * seja excluindo `milestone` e `group`, alinhado com
 * `portfolioService.getTaskStatsForProjects`. Marco não tem esforço e grupo é
 * soma dos filhos: contar qualquer um dos dois é contar duas vezes.
 *
 * A janela de datas continua olhando TODAS as folhas, marcos incluídos: um marco
 * sem duração ainda é um compromisso com data e precisa caber no enquadramento.
 */

export interface PublicScheduleStats {
  /** 0 a 100, inteiro. Folhas contáveis concluídas sobre folhas contáveis. */
  progress: number;
  startDate: Date | null;
  endDate: Date | null;
  /** Denominador do progresso, e o mesmo número exibido no contador. */
  taskCount: number;
  completedCount: number;
}

/** Tipos que não entram em contagem de progresso (BUSINESS_RULES §4). */
const NAO_CONTAVEIS = new Set(['milestone', 'group']);

const folhas = (lista: Task[]): Task[] => {
  const saida: Task[] = [];
  for (const tarefa of lista) {
    if (tarefa.children && tarefa.children.length > 0) {
      saida.push(...folhas(tarefa.children));
    } else {
      saida.push(tarefa);
    }
  }
  return saida;
};

export const computePublicScheduleStats = (tasks: Task[]): PublicScheduleStats => {
  const vazio: PublicScheduleStats = {
    progress: 0,
    startDate: null,
    endDate: null,
    taskCount: 0,
    completedCount: 0,
  };
  if (!tasks || tasks.length === 0) return vazio;

  const todasAsFolhas = folhas(tasks);
  const contaveis = todasAsFolhas.filter(tarefa => !NAO_CONTAVEIS.has(tarefa.type || 'task'));
  const completedCount = contaveis.filter(tarefa => (tarefa.progress || 0) >= 100).length;

  let startDate: Date | null = null;
  let endDate: Date | null = null;
  for (const tarefa of todasAsFolhas) {
    const inicio = tarefa.startDate || parseDateFromDB(tarefa.start_date);
    const fim = tarefa.endDate || parseDateFromDB(tarefa.end_date);
    if (inicio && (!startDate || inicio < startDate)) startDate = inicio;
    if (fim && (!endDate || fim > endDate)) endDate = fim;
  }

  return {
    progress: contaveis.length > 0 ? Math.round((completedCount / contaveis.length) * 100) : 0,
    startDate,
    endDate,
    taskCount: contaveis.length,
    completedCount,
  };
};
